import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const MedicationReconciliation = ({ medications = [] }) => {
  const [checkedMeds, setCheckedMeds] = useState(new Set());
  const [showInteractions, setShowInteractions] = useState(false);

  const handleCheckMed = (medId) => {
    const newChecked = new Set(checkedMeds);
    if (newChecked?.has(medId)) {
      newChecked?.delete(medId);
    } else {
      newChecked?.add(medId);
    }
    setCheckedMeds(newChecked);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-success text-success-foreground';
      case 'Discontinued':
        return 'bg-muted text-muted-foreground';
      case 'On Hold':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (medications?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
          <Icon name="Pill" size={32} color="var(--color-muted-foreground)" />
        </div>
        <p className="text-base font-medium text-foreground mb-2">No Medications</p>
        <p className="text-sm text-muted-foreground mb-4">Patient medication list will appear here</p>
        <Button variant="default" iconName="Plus" iconPosition="left">
          Add Medication
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            iconName="AlertTriangle"
            iconPosition="left"
            onClick={() => setShowInteractions(!showInteractions)}
          >
            Check Interactions
          </Button>
          <span className="text-sm text-muted-foreground">
            {checkedMeds?.size} selected
          </span>
        </div>
        <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
          Add Medication
        </Button>
      </div>
      {showInteractions && checkedMeds?.size > 1 && (
        <div className="bg-warning/10 border border-warning rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            <div className="flex-1">
              <p className="text-sm font-medium text-warning mb-1">Potential Drug Interaction</p>
              <p className="text-xs text-muted-foreground">
                Selected medications may have interactions. Review with pharmacist.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {medications?.map((med) => (
          <div
            key={med?.id}
            className="bg-card border border-border rounded-lg p-4 md:p-5 transition-smooth hover:shadow-elevation-2"
          >
            <div className="flex items-start gap-3 md:gap-4">
              <Checkbox
                checked={checkedMeds?.has(med?.id)}
                onChange={() => handleCheckMed(med?.id)}
                className="mt-1"
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base md:text-lg font-semibold text-foreground mb-1">
                      {med?.name}
                    </h4>
                    <p className="text-sm text-muted-foreground">{med?.dosage}</p>
                  </div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(med?.status)}`}>
                    {med?.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Calendar" size={14} />
                    <span>Started: {med?.startDate}</span>
                  </div>
                  {med?.prescriber && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Icon name="User" size={14} />
                      <span>{med?.prescriber}</span>
                    </div>
                  )}
                </div>
                
                {med?.instructions && (
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {med?.instructions}
                  </p>
                )}
                
                {med?.refillsRemaining !== undefined && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="RefreshCw" size={12} />
                    <span>{med?.refillsRemaining} refills remaining</span>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col gap-2">
                <Button variant="ghost" size="sm" iconName="Edit" />
                <Button variant="ghost" size="sm" iconName="Trash2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MedicationReconciliation;